/**
 * Chassis-page Moves tab — GPON-only (v0.34.0 pending moves).
 *
 * ``ONT-DISCOVERED-PORT-MISMATCH`` alarms correlated to the chassis
 * inventory by the backend (``GET /chassis/{id}/pending-moves``): the
 * ONT is provisioned on one PON port but the OLT keeps discovering it
 * on another. One row per mismatch with three actions:
 *
 *   - Preview — renders the exact move_onu CLI block (gap-finds the
 *     candidate target display id via ``/onus/next-available``, the
 *     same allocator the execute endpoint uses server-side).
 *   - Move — two-click ConfirmDialog, then the execute endpoint
 *     resolves ALL params server-side and queues one move_onu job;
 *     the job hands off to the InlineJobTracker via the standard
 *     ``inline-job-tracker.add`` custom event.
 *   - Mute — standard 1h alarm mute; the row hides until it lapses.
 *
 * NO unattended automation — every move is one explicit operator
 * click. Completed moves drop off on the next alarm poll (the worker
 * stamps ``cleared_at`` for mismatch alarms absent from the fresh
 * snapshot).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, BellOff, Eye, RefreshCw, Truck } from "lucide-react";
import { useState } from "react";

import { api, apiErrorMessage } from "../../../../api/client";
import { CommandPreview } from "../../../components/CommandPreview";
import { useConfirm } from "../../../components/ConfirmDialog";
import { useToast } from "../../../components/Toast";
import { InlineJobTracker } from "../InlineJobTracker";

type PendingMoveRow = {
  alarm_id: number;
  serial: string;
  provisioned: { port: string; display_id: string };
  detected_port: string;
  raised_at: string | null;
};
type PendingMovesResponse = { moves: PendingMoveRow[] };

type NextAvailableIdsResponse = {
  port: string;
  next_display_id: number;
  next_onu_id: number;
};
type MovePreview = { lines: string[] };

type ExecuteResponse = {
  job_id: string;
  serial: string;
  to_port: string;
  to_display_id: string;
};

/** Compact "how long has this mismatch been raised" label. */
function ageStr(iso: string | null): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "—";
  const min = Math.max(0, Math.floor(ms / 60_000));
  if (min < 60) return `${min}m`;
  const hours = Math.floor(min / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

const actionBtnCls =
  "inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-300 hover:bg-slate-700 disabled:opacity-50";

export function PendingMovesTab({ chassisId }: { chassisId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { confirm, confirmDialog } = useConfirm();
  // Rendered CLI blocks keyed by alarm_id — a preview stays visible in
  // the row (and embeds in the confirm dialog) until the list refetches.
  const [previews, setPreviews] = useState<Record<number, string[]>>({});

  const q = useQuery<PendingMovesResponse>({
    queryKey: ["v2", "chassis", chassisId, "pending-moves"],
    queryFn: async () => {
      const res = await api.get<PendingMovesResponse>(
        `/chassis/${chassisId}/pending-moves`,
      );
      return res.data;
    },
    refetchInterval: 30_000,
  });

  const invalidate = () => {
    qc.invalidateQueries({
      queryKey: ["v2", "chassis", chassisId, "pending-moves"],
    });
    qc.invalidateQueries({
      queryKey: ["v2", "chassis", chassisId, "pending-moves-count"],
    });
  };

  // Preview is client-side best-effort: gap-find the candidate target
  // display id with the same allocator the execute endpoint runs, then
  // render the move_onu template. The server re-resolves at execute
  // time, so a provision landing in between may shift the id by one.
  const previewMut = useMutation<string[], unknown, PendingMoveRow>({
    mutationFn: async (row) => {
      const ids = await api.get<NextAvailableIdsResponse>(
        `/chassis/${chassisId}/onus/next-available?port=${encodeURIComponent(
          row.detected_port,
        )}&count=1`,
      );
      const res = await api.post<MovePreview>(
        `/chassis/${chassisId}/maintenance/move-onu/preview`,
        {
          from_port: row.provisioned.port,
          from_display_id: row.provisioned.display_id,
          to_port: row.detected_port,
          to_display_id: String(ids.data.next_display_id),
          to_onu_id: ids.data.next_onu_id,
          serial: row.serial,
          services: [],
        },
      );
      return res.data.lines;
    },
    onSuccess: (lines, row) =>
      setPreviews((prev) => ({ ...prev, [row.alarm_id]: lines })),
    onError: (err) =>
      toast.push(apiErrorMessage(err, "Preview failed"), "error"),
  });

  const move = async (row: PendingMoveRow) => {
    const lines = previews[row.alarm_id];
    const ok = await confirm({
      title: "Confirm move_onu",
      summary:
        `move ${row.serial}\n` +
        `${row.provisioned.port}/${row.provisioned.display_id} -> port ${row.detected_port} (next free id)`,
      children: lines ? <CommandPreview text={lines.join("\n")} /> : undefined,
      confirmLabel: "Submit (destructive)",
    });
    if (!ok) return;
    try {
      const res = await api.post<ExecuteResponse>(
        `/chassis/${chassisId}/pending-moves/${row.alarm_id}/execute`,
        {},
      );
      window.dispatchEvent(
        new CustomEvent("inline-job-tracker.add", {
          detail: { jobId: res.data.job_id, kind: "move_onu" },
        }),
      );
      toast.push(
        `move_onu queued — ${row.serial} -> ${res.data.to_port}/${res.data.to_display_id}`,
        "success",
      );
      invalidate();
    } catch (err) {
      toast.push(apiErrorMessage(err, "Move failed to queue"), "error");
    }
  };

  const mute1h = useMutation({
    mutationFn: async (alarmId: number) =>
      api.post(`/alarms/${alarmId}/mute`, { duration_seconds: 3600 }),
    onSuccess: () => {
      invalidate();
      toast.push("Muted for 1h — row hidden until the mute lapses", "success");
    },
    onError: (err) => toast.push(apiErrorMessage(err, "Mute failed"), "error"),
  });

  const rows = q.data?.moves ?? [];

  return (
    <div className="flex flex-col gap-3">
      <section className="rounded-lg bg-slate-900 p-4 shadow-border">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-slate-200">
              Pending moves
            </h3>
            <p className="text-[10px] text-slate-500">
              ONTs the OLT keeps discovering on a different PON port than
              they're provisioned on. Moving deletes + recreates the ONT on
              the detected port; services carry over. Completed moves drop
              off after the next alarm poll.
            </p>
          </div>
          <button
            type="button"
            onClick={() => q.refetch()}
            disabled={q.isFetching}
            className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-2 py-1 text-[11px] text-slate-200 hover:bg-slate-700 disabled:opacity-50"
            title="Refetch the pending-move list"
          >
            <RefreshCw
              className={`h-3 w-3 ${q.isFetching ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        </div>

        {q.isLoading ? (
          <p className="text-xs text-slate-500">Loading pending moves…</p>
        ) : rows.length === 0 ? (
          <p className="text-xs text-slate-500">
            No pending moves — every discovered ONT is on its provisioned
            port.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-2 py-1">Serial</th>
                  <th className="px-2 py-1">Provisioned</th>
                  <th className="px-2 py-1">Detected</th>
                  <th className="px-2 py-1">Display ID</th>
                  <th className="px-2 py-1">Age</th>
                  <th className="px-2 py-1 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60 text-slate-300">
                {rows.map((row) => {
                  const preview = previews[row.alarm_id];
                  return (
                    <>
                      <tr key={`row-${row.alarm_id}`}>
                        <td className="px-2 py-1.5 font-mono">{row.serial}</td>
                        <td className="px-2 py-1.5 font-mono">
                          port {row.provisioned.port}
                        </td>
                        <td className="px-2 py-1.5">
                          <span className="inline-flex items-center gap-1 font-mono text-amber-300">
                            <ArrowRight className="h-3 w-3" />
                            port {row.detected_port}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 font-mono">
                          {row.provisioned.display_id}
                        </td>
                        <td
                          className="px-2 py-1.5 text-slate-400"
                          title={row.raised_at ?? undefined}
                        >
                          {ageStr(row.raised_at)}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => previewMut.mutate(row)}
                              disabled={
                                previewMut.isPending &&
                                previewMut.variables?.alarm_id === row.alarm_id
                              }
                              className={actionBtnCls}
                              title="Render the exact CLI block the move would send"
                            >
                              <Eye className="h-3 w-3" />
                              preview
                            </button>
                            <button
                              type="button"
                              onClick={() => move(row)}
                              className="inline-flex items-center gap-1 rounded border border-emerald-800 bg-emerald-900/40 px-1.5 py-0.5 text-[10px] text-emerald-300 hover:bg-emerald-900/70"
                              title="Queue a move_onu job to the detected port (confirm-gated)"
                            >
                              <Truck className="h-3 w-3" />
                              move
                            </button>
                            <button
                              type="button"
                              onClick={() => mute1h.mutate(row.alarm_id)}
                              disabled={mute1h.isPending}
                              className={actionBtnCls}
                              title="Mute this mismatch alarm for 1 hour"
                            >
                              <BellOff className="h-3 w-3" />
                              mute 1h
                            </button>
                          </div>
                        </td>
                      </tr>
                      {preview && (
                        <tr key={`preview-${row.alarm_id}`}>
                          <td colSpan={6} className="px-2 pb-2">
                            <CommandPreview
                              text={preview.join("\n")}
                              label={`move_onu — ${row.serial}`}
                            />
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {rows.length > 0 && (
          <p className="mt-2 text-[10px] text-slate-500">
            {rows.length} pending move{rows.length === 1 ? "" : "s"}. Rows
            with an in-flight move job are hidden until the job settles.
          </p>
        )}
      </section>

      {confirmDialog}

      {/* Sticky inline tracker — only renders when at least one job
          is tracked. The tracker itself handles its own visibility. */}
      <InlineJobTracker />
    </div>
  );
}
